import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import axios, { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import { queryClient } from "../App";
import { API_URL } from "../consts";
import { Clubs, Dispositivi, Risorse, RisorseTipologie } from "../prismaTypes";
import { Loading } from "../ui/Loading";
import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import DeviceThermostatIcon from "@mui/icons-material/DeviceThermostat";
import { useEffect } from "react";
import { uiStore } from "../uiStore";

interface Gruppo {
  risorsa: Risorse;
  dispositivi: Dispositivi[];
}

export function Domotica() {
  const setTitle = uiStore((state) => state.setTitle);
  useEffect(() => setTitle("Domotica"), [setTitle]);

  const { isLoading: isLoadingClub, data: club } = useQuery<{ club: Clubs }, AxiosError>(
    ["club/getByDomain", window.location.host],
    () => axios.get(API_URL + "club/getByDomain").then((res) => res.data),
    {
      refetchOnWindowFocus: false,
    }
  );

  const clubId: number | undefined = club?.club.id;

  const { isLoading: isLoadingR, data: risorse } = useQuery<
    unknown,
    AxiosError,
    { risorse: (Risorse & { tipologia: RisorseTipologie })[] }
  >(["risorse/findAll", clubId], () =>
    axios.get(API_URL + "risorse/findAll").then((res) => res.data)
  );

  const { isLoading, data } = useQuery<unknown, AxiosError, Dispositivi[]>(
    ["domotica/listUi"],
    () => axios.get(API_URL + "domotica/listUi?clubId=" + clubId).then((res) => res.data),
    {
      enabled: clubId !== undefined,
      refetchInterval: 2 * 60 * 1000,
    }
  );

  if (isLoadingClub || isLoading || data === undefined || isLoadingR) return <Loading />;

  const dispByRisorsa: Gruppo[] = [];

  data.forEach((d) => {
    const group = dispByRisorsa.find((b) => b.risorsa.id === d.idRisorse);
    if (!group) {
      const risorsa = risorse?.risorse.find((r) => r.id === d.idRisorse);
      if (risorsa)
        dispByRisorsa.push({
          risorsa,
          dispositivi: [d],
        });
    } else {
      group.dispositivi.push(d);
    }
  });

  return (
    <Grid container gap={2}>
      {dispByRisorsa.map((r) => (
        <DomoticaRisorsa key={r.risorsa.id} gruppo={r} />
      ))}
    </Grid>
  );
}
export function DomoticaRisorsa(props: { gruppo: Gruppo }) {
  const { gruppo } = props;
  return (
    <Card>
      <CardHeader title={gruppo.risorsa.denominazione} sx={{ paddingBottom: 0 }}></CardHeader>
      <CardContent>
        {gruppo.dispositivi.map((disp) => (
          <DomoticaItem disp={disp} />
        ))}
      </CardContent>
    </Card>
  );
}

export interface DeviceSetStatoDto {
  clubId: number;
  loraDevice: string;
  loraGroup: string;
  comando: "accendi" | "spegni";
}

export function DomoticaItem(props: { disp: Dispositivi }) {
  const { disp: data } = props;
  const { enqueueSnackbar } = useSnackbar();

  const { mutate } = useMutation(
    (data: DeviceSetStatoDto) => {
      return axios.post(API_URL + "domotica/comando", data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Comando Inviato", { variant: "success" });
      },
      onError: (error: AxiosError) => {
        queryClient.invalidateQueries();
        enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const update = (on: boolean) => {
    if (data.loraDevice === null) {
      enqueueSnackbar("Lora device non valido!", { variant: "error" });
      return;
    }

    mutate({
      clubId: data.idClub,
      loraDevice: data.loraDevice,
      loraGroup: data.loraGroup,
      comando: on ? "accendi" : "spegni",
    });
  };
  const on = data.stato === "on";
  const luci = data.accensioneLuci;
  return (
    <Stack gap={1} mt={1}>
      <Typography variant="subtitle1">{data.descrizione}</Typography>
      {luci && !on && (
        <LightbulbOutlined fontSize="large" sx={{ margin: "auto", fontSize: "4rem" }} />
      )}
      {luci && on && <LightbulbIcon fontSize="large" sx={{ margin: "auto", fontSize: "4rem" }} />}
      {!luci && !on && <AcUnitIcon fontSize="large" sx={{ margin: "auto", fontSize: "4rem" }} />}
      {!luci && on && (
        <DeviceThermostatIcon fontSize="large" sx={{ margin: "auto", fontSize: "4rem" }} />
      )}
      <Switch checked={on} sx={{ margin: "auto" }} onChange={(event, checked) => update(checked)} />
      <Button variant="contained" color="success" onClick={() => update(true)}>
        Accendi
      </Button>
      <Button variant="contained" color="error" onClick={() => update(false)}>
        Spegni
      </Button>
    </Stack>
  );
}
